import { Popover } from '@/components/ui';
/**
 * ThemeToggle — botão dropdown que troca entre 4 modos de tema (A3.4).
 *
 * Modos:
 *   - `manual_claro`  ☀  Tema claro (fixo)
 *   - `manual_escuro` 🌙 Tema escuro (fixo)
 *   - `automatico`    ⏱  Automático por horário (19:00–07:00 default)
 *   - `sistema`       🖥  Acompanha o sistema (prefers-color-scheme)
 *
 * UX:
 *   - Clique no botão abre menu dropdown estilo popover.
 *   - Item ativo destacado com check + cor red.
 *   - Click fora fecha menu.
 *   - Esc fecha menu.
 *
 * Acessibilidade: aria-haspopup, aria-expanded, role=menu/menuitem.
 */
import { useEffect, useRef, useState } from 'react';

import { useTheme } from '@/contexts/ThemeContext';
import type { ThemeMode } from '@/lib/dashboard/layouts-api';
import {
  SunIcon,
  MoonIcon,
  MonitorIcon,
  ClockIcon,
} from '@/components/icons';

interface ModeOption {
  value: ThemeMode;
  label: string;
  icon: (props: { className?: string }) => JSX.Element;
  hint: string;
}

const MODES: ModeOption[] = [
  {
    value: 'manual_claro',
    label: 'Claro',
    icon: SunIcon,
    hint: 'Tema claro fixo',
  },
  {
    value: 'manual_escuro',
    label: 'Escuro',
    icon: MoonIcon,
    hint: 'Tema escuro fixo',
  },
  {
    value: 'automatico',
    label: 'Automático',
    icon: ClockIcon,
    hint: 'Escuro à noite (19h–7h)',
  },
  {
    value: 'sistema',
    label: 'Sistema',
    icon: MonitorIcon,
    hint: 'Acompanha o SO',
  },
];

export function ThemeToggle({ compact = false }: { compact?: boolean }) {
  const { mode, effective, setMode } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // #307 (14/09/2026): o "clique fora fecha" que vivia aqui foi apagado. Desde
  // 06/08 o menu nasce num PORTAL (<Popover>), fora deste `ref` — o mousedown em
  // "Escuro" fechava o menu ANTES do click chegar, e o tema nunca trocava. O
  // Popover já fecha no clique fora (contando as duas árvores) e no Esc.

  // Esc fecha
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const ActiveIcon = effective === 'dark' ? MoonIcon : SunIcon;
  const currentLabel =
    MODES.find((m) => m.value === mode)?.label ?? 'Sistema';

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Tema: ${currentLabel}. Clique pra trocar.`}
        title={`Tema: ${currentLabel}`}
        data-testid="theme-toggle-button"
        className="p-3 md:p-2 rounded-lg text-ink-2 dark:text-ink-dark-2 hover:bg-paper-warm dark:hover:bg-paper-dark-warm hover:text-ink-1 dark:hover:text-ink-dark-1 transition-colors"
      >
        <ActiveIcon className="w-5 h-5" />
        {!compact && (
          <span className="sr-only">Tema: {currentLabel}</span>
        )}
      </button>

      {/* CENSO-FLUTUANTE (esteira): era `absolute` com `z-50` SOLTO — o mesmo
          número do Dialog na escada antiga, que foi o que já fez o Toast ficar
          atrás. Migrado pro <Popover> canônico: portal, flip/shift e a camada
          vinda da escada única (src/lib/camadas.ts). */}
      <Popover aberto={open} gatilhoRef={ref} onFechar={() => setOpen(false)}>
        <div
          role="menu"
          aria-label="Selecionar tema"
          data-testid="theme-toggle-menu"
          className="w-56 rounded-lg bg-paper-surface dark:bg-paper-dark-surface border border-line-soft dark:border-line-soft-dark shadow-lg py-1"
        >
          {MODES.map((opt) => {
            const Icon = opt.icon;
            const isActive = opt.value === mode;
            return (
              <button
                key={opt.value}
                type="button"
                role="menuitem"
                onClick={() => {
                  setMode(opt.value);
                  setOpen(false);
                }}
                aria-current={isActive ? 'true' : undefined}
                data-testid={`theme-mode-${opt.value}`}
                className={`w-full text-left flex items-center gap-3 px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? 'text-red bg-red-soft dark:bg-red-soft-dark'
                    : 'text-ink-1 dark:text-ink-dark-1 hover:bg-paper-warm dark:hover:bg-paper-dark-warm'
                }`}
              >
                <Icon className="w-4 h-4 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{opt.label}</div>
                  <div className="text-xs text-ink-3 dark:text-ink-dark-3 leading-tight">
                    {opt.hint}
                  </div>
                </div>
                {isActive && (
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    className="w-4 h-4 flex-shrink-0"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      </Popover>
    </div>
  );
}
